import { Box } from '@chakra-ui/react';
import styled from '@emotion/styled';
import dynamic from 'next/dynamic';
import { CSSProperties, FC, memo, ReactNode } from 'react';
import {
  BreadcrumbList,
  BreadcrumbItem,
} from '~/common/components/bread-crumb-list';
import {
  PageInner,
  Main,
  PageInnerProps,
} from '~/common/components/common-layout';
import { Footer } from '~/common/components/footer';
import { Header } from '~/common/components/header';
import { Metadata, MetaDataProps } from '~/common/components/metadata/metadata';
import { useTellerSession } from '~/common/hooks/use-auth/use-teller-session';

import { useLocalState } from './use-local-state';

// @ts-ignore
const Sidebar = dynamic(() =>
  import('~/common/components/sidebar').then((module) => module.Sidebar)
);

export interface Props {
  breadcrumbList: BreadcrumbItem[];
  metaData: MetaDataProps;
  pageInnerProps?: PageInnerProps;
  wrapperStyles?: CSSProperties;
  children?: ReactNode;
}

const Wrapper = styled.div<{
  openInAppFloating?: boolean;
}>`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

export const MinimalLayout: FC<Props> = memo(
  ({ breadcrumbList, metaData, pageInnerProps, wrapperStyles, children }) => {
    const { session } = useTellerSession();

    const { wasAlreadyVip, sidebar, notifications, handleOnClickInAppHeader } =
      useLocalState();
    return (
      <>
        <Metadata {...metaData} />
        <Wrapper style={wrapperStyles}>
          <Header
            showOpenInApp={false}
            wasAlreadyVip={wasAlreadyVip}
            openInAppHeaderFixed={false}
            onSidebarOpen={sidebar.handleSidebarOpen}
            showNotifications={!!session?.tellerUser && !session?.isAnonymous}
            notificationsCount={notifications.notificationCount}
            notificationItemList={notifications.notificationItemList}
            onClickOpenInAppButton={handleOnClickInAppHeader}
          />
          <PageInner {...pageInnerProps}>
            <Main openInAppHeaderFixed={false}>{children}</Main>
          </PageInner>
          <BreadcrumbList items={breadcrumbList} />
          <Footer />
        </Wrapper>
        <Box
          display={{
            base: 'block',
            lg: 'none',
            xl: 'none',
            '2xl': 'none',
          }}
        >
          <Sidebar
            // @ts-ignore
            vipPageLink={sidebar.vipPageLink}
            open={sidebar.sidebarOpen}
            onClose={sidebar.handleSidebarClose}
          />
        </Box>
      </>
    );
  }
);

MinimalLayout.displayName = 'MinimalLayout';
